import { EDITOR_FONTS, getFontIds } from "../../../../../const/fonts";

// Global Quill configuration for custom font formats
let customFontFormatRegistered = false;
let quillFontCssInjected = false;

export const registerCustomFontFormat = async (fonts: string[]) => {
  if (customFontFormatRegistered) return;

  try {
    const { Quill } = await import("react-quill");

    // First, just update the whitelist for the existing Font format
    const Font = Quill.import("formats/font");
    Font.whitelist = [...fonts, "sans-serif"];

    // Create a custom inline blot to handle font classes
    const Inline = Quill.import("blots/inline");

    class FontClassBlot extends Inline {
      static blotName = "fontClass";
      static tagName = "SPAN";

      static create(value: string) {
        const node = super.create();
        if (value && fonts.includes(value)) {
          node.setAttribute("class", `ql-font-${value}`);
        }
        return node;
      }

      static formats(domNode: HTMLElement) {
        const className = domNode.getAttribute("class");
        if (className && className.startsWith("ql-font-")) {
          const fontName = className.replace("ql-font-", "");
          if (fonts.includes(fontName)) {
            return fontName;
          }
        }
        return false;
      }

      format(name: string, value: unknown) {
        if (name === FontClassBlot.blotName && value) {
          this.domNode.setAttribute("class", `ql-font-${value}`);
        } else {
          super.format(name, value);
        }
      }
    }

    Quill.register(FontClassBlot);

    // Dynamically inject CSS for font classes from the registry (one-time)
    if (!quillFontCssInjected) {
      const styleEl = document.createElement("style");
      styleEl.setAttribute("data-quill-fonts", "true");
      const rules: string[] = [];

      EDITOR_FONTS.forEach(({ id }) => {
        if (id === "sans-serif") return;
        rules.push(".ql-font-" + id + " { font-family: '" + id + "', sans-serif !important; }");
        rules.push(".ql-editor .ql-font-" + id + " { font-family: '" + id + "', sans-serif !important; }");
      });

      styleEl.textContent = rules.join("\n");
      document.head.appendChild(styleEl);
      quillFontCssInjected = true;
    }
    customFontFormatRegistered = true;
  } catch (error) {
    console.error("Error registering custom font format:", error);
  }
};

export const initializeQuillFontSupport = async () => {
  // Include all fonts from the centralized registry so Quill preserves them on initial parse
  await registerCustomFontFormat(getFontIds());
};
